import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Globe, ArrowLeft, BookOpen, Award, TrendingUp, Trophy, Flame, Bell } from 'lucide-react';
import { programAPI } from '../../services/programApi';
import { toast } from '../../hooks/use-toast';

const ProgramDashboard = () => {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const isLoggedIn = !!localStorage.getItem('authToken');

  useEffect(() => {
    fetchLanguages();
    if (isLoggedIn) {
      fetchLeaderboard();
      fetchNotifications();
    }
  }, []);

  const fetchLanguages = async () => {
    try {
      const data = await programAPI.getLanguages();
      setLanguages(data.languages);
    } catch (error) {
      console.error('Error fetching languages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load program languages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const data = await programAPI.getGlobalLeaderboard(5);
      setLeaderboard(data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const data = await programAPI.getNotifications();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading programs...</p>
      </div>
    );
  }

  // Split languages into two rows
  const europeanLanguages = languages.filter(l => ['spanish', 'french', 'german', 'italian'].includes(l.id));
  const indianLanguages = languages.filter(l => ['hindi', 'tamil', 'kannada', 'telugu', 'malayalam'].includes(l.id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">15-Day Program</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {notifications.length > 0 && (
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {notifications.length}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Guest Banner */}
        {!isLoggedIn && (
          <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="guest-program-banner">
            <p className="text-amber-800 text-sm font-medium">Create a free account to track progress, take tests, and join the leaderboard</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 ml-4 shrink-0" onClick={() => navigate('/register')}>
              Sign Up Free
            </Button>
          </div>
        )}
        {/* Notifications Banner */}
        {notifications.length > 0 && notifications[0].type !== 'welcome' && (
          <div className="mb-8 p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Flame className="h-6 w-6 text-orange-500" />
                <div>
                  <p className="font-semibold text-slate-900">{notifications[0].title}</p>
                  <p className="text-sm text-slate-600">{notifications[0].message}</p>
                </div>
              </div>
              {notifications[0].action && (
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate(notifications[0].action)}
                >
                  Continue
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <Award className="h-16 w-16 text-amber-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Master a Language in 15 Days
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Comprehensive step-by-step program to take you from beginner to intermediate level.
            Learn grammar, vocabulary, and practice with daily tests.
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="border-2">
            <CardHeader>
              <BookOpen className="h-8 w-8 text-amber-600 mb-2" />
              <CardTitle className="text-lg">Structured Learning</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                15 carefully designed lessons covering grammar, vocabulary, and practical exercises
              </p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader>
              <TrendingUp className="h-8 w-8 text-sky-600 mb-2" />
              <CardTitle className="text-lg">Daily Testing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Dynamic AI-generated tests after each lesson. Score 70% to unlock the next day
              </p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader>
              <Award className="h-8 w-8 text-emerald-600 mb-2" />
              <CardTitle className="text-lg">Final Certification</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">
                Comprehensive final test covering all key concepts. Retake as many times as needed
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Language Selection */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Choose Your Language</h2>
          
          {/* European Languages */}
          <h3 className="text-lg font-semibold text-slate-700 mb-4">European Languages</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {europeanLanguages.map((lang) => (
              <Card 
                key={lang.id}
                data-testid={`language-card-${lang.id}`}
                className={`border-2 cursor-pointer transition-all ${
                  lang.available 
                    ? 'hover:border-amber-400 hover:shadow-lg' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => lang.available && navigate(`/program/${lang.id}`)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{lang.name}</CardTitle>
                  <CardDescription className="text-sm">
                    {lang.native_name || lang.name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lang.available ? (
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-sm">
                      Start Program
                    </Button>
                  ) : (
                    <Button className="w-full text-sm" disabled>
                      Coming Soon
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Indian Languages */}
          <h3 className="text-lg font-semibold text-slate-700 mb-4">Indian Languages</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {indianLanguages.map((lang) => (
              <Card 
                key={lang.id}
                data-testid={`language-card-${lang.id}`}
                className={`border-2 cursor-pointer transition-all ${
                  lang.available 
                    ? 'hover:border-amber-400 hover:shadow-lg' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => lang.available && navigate(`/program/${lang.id}`)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{lang.name}</CardTitle>
                  <CardDescription className="text-sm">
                    {lang.native_name || lang.name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lang.available ? (
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-sm">
                      Start Program
                    </Button>
                  ) : (
                    <Button className="w-full text-sm" disabled>
                      Coming Soon
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Leaderboard Preview */}
        {leaderboard.length > 0 && (
          <Card className="border-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Trophy className="h-6 w-6 text-yellow-500" />
                  <CardTitle>Global Leaderboard</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/program/leaderboard')}>
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.slice(0, 5).map((entry, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-slate-300 text-slate-700' :
                        index === 2 ? 'bg-amber-600 text-white' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {entry.rank}
                      </span>
                      <span className="font-medium">{entry.username}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-slate-600">
                      <span className="flex items-center space-x-1">
                        <Flame className="h-4 w-4 text-orange-500" />
                        <span>{entry.streak_days} days</span>
                      </span>
                      <span className="font-semibold text-amber-600">{entry.score} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProgramDashboard;